import { ErrorCode, type FileError } from "react-dropzone";
import { normalizeUploadFilename, type CollectedUploadFile } from "@/views/drive/upload/types";
import {
  ACCEPTED_EXTENSIONS,
  DEFAULT_UPLOAD_WEIGHT,
  MAX_UPLOAD_SIZE,
  MAX_UPLOAD_WEIGHT,
  MIN_UPLOAD_WEIGHT,
  UPLOAD_WEIGHT_LOG_FACTOR,
} from "./consts";
import { BackendStatus, FileWithProgress } from "./types";
import { isRejectionCode } from "./errorMessages";

const ACCEPTED_FILE_EXTENSIONS = Object.values(ACCEPTED_EXTENSIONS).flat();
const ACCEPTED_FILE_TYPES = Object.entries(ACCEPTED_EXTENSIONS).flatMap(([mimeType, extensions]) => [
  mimeType,
  ...extensions,
]);

export const isValidFileType = (file: File): boolean => {
  const name = normalizeUploadFilename(file.name).toLowerCase();
  return ACCEPTED_FILE_EXTENSIONS.some(extension => name.endsWith(extension));
};

export const formatFileErrors = (errors: readonly FileError[]): string =>
  errors
    .map(error => error.message)
    .join(", ")
    .replace(`than ${MAX_UPLOAD_SIZE} bytes`, `than ${MAX_UPLOAD_SIZE / 1024 / 1024} MB`);

export const validateUploadFile = (file: File): FileError[] => {
  const errors: FileError[] = [];
  if (!isValidFileType(file)) {
    errors.push({
      code: ErrorCode.FileInvalidType,
      message: `File type must be one of ${ACCEPTED_FILE_TYPES.join(", ")}`,
    });
  }
  if (file.size < 10) {
    errors.push({ code: ErrorCode.FileTooSmall, message: "File is smaller than 10 bytes" });
  }
  if (file.size > MAX_UPLOAD_SIZE) {
    errors.push({
      code: ErrorCode.FileTooLarge,
      message: `File is larger than ${MAX_UPLOAD_SIZE} bytes`,
    });
  }
  return errors;
};

export const toUploadRows = (files: readonly CollectedUploadFile[]): FileWithProgress[] =>
  files.map(({ file, relativePath }) => {
    const errors = validateUploadFile(file);
    return {
      clientId: crypto.randomUUID(),
      file,
      relativePath,
      progress: 0,
      status: errors.length > 0 ? ("error" as const) : ("pending" as const),
      error: errors.length > 0 ? formatFileErrors(errors) : undefined,
    };
  });

export const processNewFiles = (files: File[]): FileWithProgress[] =>
  toUploadRows(files.map(file => ({ file, relativePath: normalizeUploadFilename(file.name), directoryPath: "" })));

const clampFraction = (value: number): number => (Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0);

export const calculateAdaptiveUploadWeight = (sizeBytes: number): number => {
  const safeSizeBytes = Number.isFinite(sizeBytes) ? Math.max(0, sizeBytes) : 0;
  const sizeMiB = Math.max(safeSizeBytes / (1024 * 1024), 1);
  return Math.min(
    MAX_UPLOAD_WEIGHT,
    Math.max(MIN_UPLOAD_WEIGHT, MIN_UPLOAD_WEIGHT + UPLOAD_WEIGHT_LOG_FACTOR * Math.log2(sizeMiB)),
  );
};

export const calculateTransferProgress = (transferFraction: number, sizeBytes: number): number =>
  clampFraction(transferFraction) * calculateAdaptiveUploadWeight(sizeBytes) * 100;

export const calculateUploadProgress = (backendProgress: number, sizeBytes?: number): number => {
  const uploadWeight = sizeBytes === undefined ? DEFAULT_UPLOAD_WEIGHT : calculateAdaptiveUploadWeight(sizeBytes);
  return (uploadWeight + clampFraction(backendProgress) * (1 - uploadWeight)) * 100;
};

export const normalizeDisplayProgress = (
  file: Pick<FileWithProgress, "progress" | "status" | "error_code">,
): number => {
  if (file.status === "pending" || (file.status === "error" && isRejectionCode(file.error_code))) return 0;
  if (!Number.isFinite(file.progress)) return 0;
  return Math.round(Math.min(100, Math.max(0, file.progress)));
};

export const mapBackendStatusToFileStatus = (
  backendStatus: BackendStatus,
  progress: number,
): FileWithProgress["status"] => {
  switch (backendStatus) {
    case "completed":
      return "completed";
    case "failed":
      return "error";
    case "pending":
      return "pending";
    case "in_progress":
    case "partially_failed":
      return progress >= 1 ? "completed" : "uploading";
    default:
      return "uploading";
  }
};
